import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Discards the cached list and refetches the current view from the backend. Purely presentational — the
 * caller supplies the behaviour by spreading one of the per-list refresh hooks
 * (`useRefreshInvoices` / `useRefreshDrafts` / `useRefreshOutbound`), so the same button serves every
 * list. Spins while a request is in flight, and is disabled meanwhile to avoid stacking refetches.
 * [onClick] runs alongside the refetch (the list views use it to scroll back to the top).
 */
export function RefreshButton({
  refresh,
  isFetching,
  onClick,
}: {
  refresh: () => unknown;
  isFetching: boolean;
  onClick?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={t('invoices.refresh')}
      title={t('invoices.refresh')}
      disabled={isFetching}
      onClick={() => {
        onClick?.();
        void refresh();
      }}
    >
      <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
    </Button>
  );
}
